import React, { useState, useEffect, useRef, useMemo } from 'react';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
// --- SVG ICONS ---
const CloseIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const TrashIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
  </svg>
);

const MaximizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
  </svg>
);

const MinimizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
  </svg>
);

const SaveIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"></path>
    <polyline points="17 21 17 13 7 13 7 21"></polyline>
    <polyline points="7 3 7 8 15 8"></polyline>
  </svg>
);

// Success Icon Component
const SuccessIcon = ({ size = 48, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
    <polyline points="22 4 12 14.01 9 11.01"></polyline>
  </svg>
);

const API_URL = '/api';

// --- SEARCHABLE DROPDOWN COMPONENT ---
const SearchableDropdown = ({ 
  options = [], 
  value, 
  onChange, 
  placeholder = "Select",
  error = false,
  width = "w-full",
  getOptionLabel = (option) => option.name || option.SiteIndentNo || option.SurveyBOQNo || option.label || option.toString(),
  getOptionValue = (option) => option.value || option.SiteIndentID || option.SurveyBOQID || option.id || option.toString(),
  getOptionSecondaryLabel = (option) => option.code || option.ProjectNo || option.ProjectCode || option.id || "",
  disabled = false,
  className = "",
  isLoading = false 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownListRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({ top: rect.bottom, left: rect.left, width: rect.width });
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleUpdatePosition = () => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setPosition({ top: rect.bottom, left: rect.left, width: rect.width });
      }
    };
    window.addEventListener('resize', handleUpdatePosition);
    window.addEventListener('scroll', handleUpdatePosition);
    return () => { window.removeEventListener('resize', handleUpdatePosition); window.removeEventListener('scroll', handleUpdatePosition); };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && dropdownListRef.current && highlightedIndex >= 0) {
      const items = dropdownListRef.current.querySelectorAll('.dropdown-item');
      if (items[highlightedIndex]) items[highlightedIndex].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [highlightedIndex, isOpen]);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setSearchTerm('');
      setHighlightedIndex(-1);
      setTimeout(() => { searchInputRef.current.focus(); }, 0);
    }
  }, [isOpen]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    const term = searchTerm.toLowerCase();
    return options.filter(option => {
      const label = (getOptionLabel(option) || '').toString().toLowerCase();
      const secondaryLabel = (getOptionSecondaryLabel(option) || '').toString().toLowerCase();
      return label.includes(term) || secondaryLabel.includes(term);
    });
  }, [options, searchTerm, getOptionLabel, getOptionSecondaryLabel]);

  const handleKeyDown = (e) => {
    if (!isOpen) { if (e.key === 'ArrowDown' || e.key === 'Enter') { setIsOpen(true); return; } return; }
    switch (e.key) {
      case 'ArrowDown': e.preventDefault(); setHighlightedIndex(prev => { if (prev < filteredOptions.length - 1) return prev + 1; else if (filteredOptions.length > 0) return 0; return prev; }); break;
      case 'ArrowUp': e.preventDefault(); setHighlightedIndex(prev => { if (prev > 0) return prev - 1; else if (filteredOptions.length > 0) return filteredOptions.length - 1; return prev; }); break;
      case 'Enter': e.preventDefault(); if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) handleSelectOption(filteredOptions[highlightedIndex]); break;
      case 'Escape': setIsOpen(false); break; default: break;
    }
  };

  const handleSelectOption = (option) => { onChange(getOptionValue(option)); setIsOpen(false); setSearchTerm(''); setHighlightedIndex(-1); };
  useEffect(() => { const handleClickOutside = (event) => { if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setIsOpen(false); }; document.addEventListener('mousedown', handleClickOutside); return () => { document.removeEventListener('mousedown', handleClickOutside); }; }, []);
  const selectedOption = options.find(option => getOptionValue(option) === value);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div
        className={`${width} border rounded-lg px-3 py-2 cursor-pointer flex justify-between items-center ${isOpen ? 'ring-1 ring-[#BDDDFC]' : ''} ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
        style={{ borderColor: error ? 'rgb(248, 113, 113)' : 'rgb(230, 217, 204)' }}
        onClick={() => !disabled && !isLoading && setIsOpen(!isOpen)}
        ref={triggerRef}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        <span className="truncate">
           {isLoading && <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full mr-2 inline-block"></div>}
          {selectedOption ? getOptionLabel(selectedOption) : placeholder}
        </span>
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
      
      {isOpen && (
        <div 
          className="fixed z-[9999] bg-white border border-[#E2E8F0] rounded-lg shadow-lg"
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            width: `${position.width}px`,
          }}
        >
          <div className="p-2 border-b border-[#E2E8F0]">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search..."
              className="w-full px-2 py-1 border border-[#E2E8F0] rounded focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>
          
          <div 
            className="max-h-60 overflow-y-auto" 
            ref={dropdownListRef}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={getOptionValue(option)}
                  className={`dropdown-item px-3 py-2 cursor-pointer hover:bg-[#F1F5F9] ${
                    highlightedIndex === index ? 'bg-[#BDDDFC]' : ''
                  } ${value === getOptionValue(option) ? 'bg-[#E6F0FF]' : ''}`}
                  onClick={() => handleSelectOption(option)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <div className="font-medium">{getOptionLabel(option)}</div>
                  {getOptionSecondaryLabel(option) && (
                    <div className="text-xs text-gray-500">{getOptionSecondaryLabel(option)}</div>
                  )}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-gray-500">No options found</div>
            )}
          </div>
        </div>
      )}
      
      {error && <span className="text-xs text-red-500">*</span>}
    </div>
  );
};

// --- MAIN FORM COMPONENT ---
const MaterialIssueFormModal = ({ open, onClose, initialData, onSubmit, onFormSuccess }) => {
  
  // --- STATE ---
  const initialHeader = {
    IssueHeaderID: null,
    IssueHeaderNo: '',
    SiteIndentID: null,
    SiteIndentNo: '',
    IssueDate: new Date().toISOString().split('T')[0],
    VendorCode: '', 
    SurveyBOQID: '', 
    ProjectHeaderId: '', 
    ProjectNo: '',
    FromFeederId: '', 
    ToFeederId: '', 
    Circle: '', 
    District: '', 
    Division: '', 
    SubDivision: '', 
    SubStation: '', 
    FeederEngineerName: '', 
    Remarks: '',
    BcSuccess: false,
    ActivityId: null,
    SubActivityId: null,
     ActivityName: '',      
    SubActivityName: '' 
  };

  const initialLine = {
    SiteIndentLineID: null,
    SurveyBOQLineID: null,
    ProductCode: '',
    FeederId: '',
    AvailableStock: 0, 
    TaskNo: '',
    Description: '',
    Remark: '',
    UnitOfMeasure: 'NOS',
    SurveyQty: 0, 
    IndentQty: 0, 
    QtyToIssue: 0,
    IssueLineNumber: 100,
    RemainingQty: 0,
    OriginalQtyToIssue: 0 ,
    BcSuccess: false,
    ActivityId: null,       // <--- ADD THIS
    SubActivityId: null 
  };

  const [header, setHeader] = useState(initialHeader);
  const [lines, setLines] = useState([initialLine]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [isLoadingStockData, setIsLoadingStockData] = useState(false);
  const [pendingStockFetch, setPendingStockFetch] = useState(null);

  // Dropdown Data
  const [projects, setProjects] = useState([]);
  const [feeders, setFeeders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [siteIndents, setSiteIndents] = useState([]); 
  const [surveyBOQs, setSurveyBOQs] = useState([]); 
  const [nextIssueNumber, setNextIssueNumber] = useState('');
   const [projectActivities, setProjectActivities] = useState([]);
  // ADD THIS STATE:
  const [highlightedRows, setHighlightedRows] = useState([]);
   const isHeaderLocked = header.BcSuccess === true || header.BcSuccess === 1;

    // --- TOAST / ALERT STATE ---
  const [toast, setToast] = useState({
    open: false,
    type: 'error',
    title: '',
    message: ''
  });

  // Refs for Resizing
  const [columnWidths, setColumnWidths] = useState({
    lineNo: 80, productCode: 180, availStock: 200,taskNo: 100, feeder: 130, desc: 280,
    surveyQty: 200, indentQty: 200, remainingQty: 200, qtyToIssue: 200, uom: 80, action: 80, activity: 200,subActivity: 200,remark: 200
  });
  const resizingRef = useRef(null);

  // --- EFFECTS ---
  useEffect(() => {
    const fetchData = async () => {
      if (open) {
        setIsMaximized(false);
        resetForm();
        fetchProjects();
        fetchFeeders();
        fetchVendors();
        fetchSurveyBOQs(); 
        fetchFilteredSiteIndents(); 

        const isEditMode = initialData && initialData.header && initialData.header.IssueHeaderID;
        if (!isEditMode) {
          fetchNextIssueNumber();
        }

        if (initialData) {
          const sourceData = initialData.header ? initialData.header : initialData;

           const formattedActivity = sourceData.ActivityCode 
            ? `${sourceData.ActivityCode} - ${sourceData.ActivityDesc || ''}` 
            : '';
          
          const formattedSubActivity = sourceData.SubActivityCode 
            ? `${sourceData.SubActivityCode} - ${sourceData.SubActivityDesc || ''}` 
            : '';

          setHeader(prev => ({ ...initialHeader, ...sourceData, IssueDate: sourceData.IssueDate ? sourceData.IssueDate.split('T')[0] : new Date().toISOString().split('T')[0],
            ActivityName: formattedActivity,
            SubActivityName: formattedSubActivity
           }));
          
          let editLines = [];
          if (sourceData.Lines) editLines = sourceData.Lines;
          else if (initialData.lines) editLines = initialData.lines;
          
          // --- UPDATED LOGIC: FETCH REMAINING QTY FOR EDIT MODE ---
          if (editLines.length > 0) {
             const linesWithRemaining = await Promise.all(editLines.map(async (l) => {
               const currentHeaderId = sourceData.SiteIndentID; 
               const remQty = await fetchLineRemainingQty(currentHeaderId, l.SiteIndentLineID);
               return {
                 ...l,
                 RemainingQty: remQty || 0,
                 OriginalQtyToIssue: parseFloat(l.QtyToIssue) || 0 // <--- STORE ORIGINAL DB VALUE
               };
             }));
             setLines(linesWithRemaining);
              if (sourceData.FromFeederId && sourceData.ProjectNo && linesWithRemaining.length > 0) {
                setPendingStockFetch({
                  fromFeederId: sourceData.FromFeederId,
                  lines: linesWithRemaining,
                  projectNo: sourceData.ProjectNo
                });
             }
          } else {
            setLines([initialLine]);
          }
        }
      }
    };

    fetchData();
  }, [open, initialData]);

    // --- EFFECT: FETCH STOCK DATA WHEN FEEDERS ARE READY ---
  useEffect(() => {
    if (pendingStockFetch && feeders.length > 0) {
      console.log('✅ Feeders loaded! Fetching stock data...');
      
      fetchStockDataFromBC(
        pendingStockFetch.fromFeederId, 
        pendingStockFetch.lines, 
        pendingStockFetch.projectNo
      );
      
      // Clear the pending flag after triggering
      setPendingStockFetch(null);
    }
  }, [feeders, pendingStockFetch]);

  // --- DYNAMIC FILTERING EFFECT ---
  useEffect(() => {
    fetchFilteredSiteIndents();
  }, [header.VendorCode, header.ProjectNo, header.FromFeederId, header.ToFeederId, header.SurveyBOQID]);


  // --- API HANDLERS ---
  const fetchNextIssueNumber = async () => {
    try {
      const res = await fetch(`${API_URL}/material-issue/next-number`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
      const data = await res.json();
      if (data && data.nextHeaderNo) setNextIssueNumber(data.nextHeaderNo);
    } catch (e) {  setNextIssueNumber('MI-1'); }
  };

  const fetchVendors = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/vendors`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      setVendors(Array.isArray(data) ? data : []);
    } catch (e) { setVendors([]); }
  };

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/project-filter`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      setProjects(Array.isArray(data) ? data : []);
    } catch (e) { setProjects([]); }
  };

  const fetchFeeders = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/feeder-filter`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      setFeeders(Array.isArray(data) ? data : []);
    } catch (e) { setFeeders([]); }
  };

  const fetchSurveyBOQs = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/survey-boq-filter`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      setSurveyBOQs(Array.isArray(data) ? data : []);
    } catch (e) {  setSurveyBOQs([]); }
  };
    const fetchActivities = async (projectNo) => {
    if (!projectNo) {
      setProjectActivities([]);
      return;
    }
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/activity-master/project/${projectNo}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch activities');
      const data = await res.json();
      setProjectActivities(data || []);
    } catch (e) {
      console.error("Error fetching activities:", e);
      setProjectActivities([]);
    }
  };
    // ADD THIS NEW EFFECT
  useEffect(() => {
    if (header.ProjectNo) {
      fetchActivities(header.ProjectNo);
    } else {
      setProjectActivities([]);
    }
  }, [header.ProjectNo]);

  const fetchLineRemainingQty = async (headerId, lineId) => {
    if (!headerId || !lineId) return 0;
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/site-indent/${headerId}/line/${lineId}/remaining-qty`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) return 0;
      const data = await res.json();
      return data.data ? (data.data.RemainingQty || 0) : 0;
    } catch (e) {
      // console.error("Error fetching remaining qty:", e);
      return 0;
    }
  };

  const fetchFilteredSiteIndents = async () => {
    setFilterLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const params = new URLSearchParams();
      if (header.VendorCode) params.append('vendorCode', header.VendorCode);
      if (header.ProjectNo) params.append('projectNo', header.ProjectNo);
      if (header.FromFeederId) params.append('fromFeederId', header.FromFeederId);
      if (header.ToFeederId) params.append('toFeederId', header.ToFeederId);
      if (header.SurveyBOQID) params.append('surveyBoqId', header.SurveyBOQID);

      const isEditMode = initialData && initialData.header && initialData.header.IssueHeaderID;
      if (isEditMode && initialData.header.SiteIndentID) {
        params.append('currentSiteIndentId', initialData.header.SiteIndentID);
      }

      const res = await fetch(`${API_URL}/site-indent-filter-Issue-data?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setSiteIndents(Array.isArray(data) ? data : []);
    } catch (e) {
      // console.error(e);
      setSiteIndents([]);
    } finally {
      setFilterLoading(false);
    }
  };

    const fetchStockDataFromBC = async (fromFeederId, currentLines = null, projectNo = null) => {
    const linesToUse = currentLines || lines;
    const effectiveProjectNo = projectNo || header.ProjectNo;

    if (!effectiveProjectNo || !fromFeederId || linesToUse.length === 0) return;

    const fromFeeder = feeders.find(f => f.FeederId == fromFeederId);
    if (!fromFeeder || !fromFeeder.Code) return;

    // Prepare items array
    const items = linesToUse
      .map((line, index) => ({
        Item_No: line.ProductCode,
        lineIndex: index
      }))
      .filter(item => item.Item_No);

    if (items.length === 0) return;
    setIsLoadingStockData(true);

    try {
      const token = localStorage.getItem('authToken');
      
      // Single API call with all items
      const response = await fetch(`${API_URL}/mapping/site-indent/fetch-item-stock`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
           Project_No: effectiveProjectNo,
          From_Site: fromFeeder.Code,
          items: items
        })
      });

      const data = await response.json();
      
      if (data.success && data.data.items) {
        const stockMap = new Map();
        data.data.items.forEach(item => {
          stockMap.set(item.Item_No, item);
        });

        setLines(prevLines => {
          return prevLines.map(line => {
            const stockItem = stockMap.get(line.ProductCode);
            if (stockItem) {
              return {
                ...line, 
                AvailableStock: stockItem.AvailableStock,
                // StockOnAvailableIndent: stockItem.StockOnAvailableIndent // Optional: if you want this field too
              };
            }
            return line;
          });
        });
         showToast(`Stock data updated for ${data.data.items.length} items`, 'success', 'Stock Data Loaded');
      }
    } catch (error) {
      console.error("Error fetching stock data:", error);
    }finally{
      setIsLoadingStockData(false);
    }
  };

  const handleSiteIndentChange = async (siteIndentID) => {
    const selectedSI = siteIndents.find(si => si.SiteIndentID == siteIndentID);

    if (selectedSI) {
      setHeader(prev => ({
        ...prev,
        SiteIndentID: selectedSI.SiteIndentID,
        SiteIndentNo: selectedSI.SiteIndentNo,
        SurveyBOQID: selectedSI.SurveyBOQID || '', 
        ProjectNo: selectedSI.ProjectNo,
        ProjectHeaderId: selectedSI.ProjectHeaderId,
        VendorCode: selectedSI.VendorCode,
        FromFeederId: selectedSI.FromFeederId,
        ToFeederId: selectedSI.ToFeederId,
        Circle: selectedSI.Circle || '',
        District: selectedSI.District || '',
        Division: selectedSI.Division || '', 
        SubDivision: selectedSI.SubDivision || '', 
        SubStation: selectedSI.SubStation || '', 
        FeederEngineerName: selectedSI.FeederEngineerName || '' ,
        ActivityId: selectedSI.ActivityId || null,       // <--- ADD THIS
        SubActivityId: selectedSI.SubActivityId || null ,
        ActivityName: selectedSI.ActivityCode 
          ? `${selectedSI.ActivityCode} - ${selectedSI.ActivityDesc || ''}` 
          : '',
        SubActivityName: selectedSI.SubActivityCode 
          ? `${selectedSI.SubActivityCode} - ${selectedSI.SubActivityDesc || ''}` 
          : ''
        
      }));

      try {
        const token = localStorage.getItem('authToken');
        const res = await fetch(`${API_URL}/site-indent-filter-Issue/${selectedSI.SiteIndentID}`, {
           headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) throw new Error('Failed to fetch SI Details');
        const siData = await res.json();

        if (siData.lines && Array.isArray(siData.lines)) {
           const linesWithRemaining = await Promise.all(siData.lines.map(async (siLine, index) => {
             const remQty = await fetchLineRemainingQty(selectedSI.SiteIndentID, siLine.SiteIndentLineID);
             
             return {
               ...initialLine,
               SiteIndentLineID: siLine.SiteIndentLineID,
               ProductCode: siLine.ProductCode || siLine.ItemNo,
               Description: siLine.Description || siLine.ItemNo,
               TaskNo: siLine.TaskNo || '',
               UnitOfMeasure: siLine.UnitOfMeasure || 'NOS',
               SurveyQty: siLine.Quantity || 0, 
               IndentQty: siLine.QtyToIssue || 0, 
                Remark: siLine.Remark || '',
               QtyToIssue:  0,
               IssueLineNumber: 100 + (index * 100),
               RemainingQty: remQty > 0 ? remQty : 0,
               OriginalQtyToIssue: 0 ,
               ActivityId: siLine.ActivityId || null,
               SubActivityId: siLine.SubActivityId || null
             };
           }));
           
           setLines(linesWithRemaining);
            if (selectedSI.FromFeederId && selectedSI.ProjectNo && linesWithRemaining.length > 0) {
              setPendingStockFetch({
                fromFeederId: selectedSI.FromFeederId,
                lines: linesWithRemaining,
                projectNo: selectedSI.ProjectNo
              });
           }
        } else {
          setLines([initialLine]);
        }
      } catch (err) {
        // console.error("Error fetching SI lines", err);
        // alert("Could not load lines for selected Site Indent");
        showToast('error', 'Error', 'Could not load lines for selected Site Indent');
      }
    }
  };

  const addLine = () => {
    const lastLine = lines[lines.length - 1];
    const nextLineNumber = lastLine ? parseInt(lastLine.IssueLineNumber) + 100 : 100;
    setLines([...lines, { ...initialLine, IssueLineNumber: nextLineNumber }]);
  };
  
  const removeLine = (index) => {
    if (lines.length > 1) setLines(lines.filter((_, i) => i !== index));
  };

   const handleLineChange = (index, field, value) => {
    // --- UPDATED LOGIC FOR QtyToIssue ---
    if (field === 'QtyToIssue') {
      const line = lines[index];
      const indentQty = parseFloat(line.IndentQty) || 0;
      const remainingQty = parseFloat(line.RemainingQty) || 0;
      
      // 1. Get the quantity that was originally saved in the database for this line
      const originalQty = parseFloat(line.OriginalQtyToIssue) || 0;
      
      let newVal = parseFloat(value);
      if (isNaN(newVal)) newVal = 0;

      // 2. Calculate the true maximum limit.
      // Logic: The API returns what is LEFT (remainingQty). 
      // We must add back what we ALREADY HAVE (originalQty) to get the total limit for this specific line.
      const maxAllowedLimit = originalQty + remainingQty;

      // 3. The final cap is the lower of the Indent Qty or the Calculated Limit
      const maxAllowed = Math.min(indentQty, maxAllowedLimit);

      // 4. Check if new value exceeds max allowed
      if (newVal > maxAllowed) {
        showToast(
          `Cannot exceed limit. Max allowed: ${maxAllowed.toFixed(6)} (Limit: ${maxAllowedLimit.toFixed(6)}, Indent: ${indentQty.toFixed(6)})`,
          'error',
          'Validation Error'
        );
        newVal = maxAllowed;
      }
      
      // Prevent negative values
      if (newVal < 0) newVal = 0;

      const newLines = [...lines];
      newLines[index][field] = newVal;
      setLines(newLines);
      return;
    }

    // --- DEFAULT LOGIC ---
    const newLines = [...lines];
    newLines[index][field] = value;
    setLines(newLines);
  };

  const resetForm = () => {
    setHeader(initialHeader);
    setLines([initialLine]);
    setNextIssueNumber('');
  };

  const startResize = (columnKey, startX) => {
    resizingRef.current = { columnKey, startX, startWidth: columnWidths[columnKey] };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', stopResize);
  };
  const handleMouseMove = (e) => {
    if (!resizingRef.current) return;
    const { columnKey, startX, startWidth } = resizingRef.current;
    const delta = e.clientX - startX;
    setColumnWidths(prev => ({ ...prev, [columnKey]: Math.max(60, startWidth + delta) }));
  };
  const stopResize = () => {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', stopResize);
    resizingRef.current = null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

     if (!header.SiteIndentID) {
      showToast('Site Indent is required.', 'error', 'Required Field');
      return;
    }

    // if (!header.IssueDate) {
    //   showToast('Issue Date is required.', 'error', 'Required Field');
    //   return;
    // }
    setIsSubmitting(true);
    setLoading(true);
    
    try {
      const token = localStorage.getItem('authToken');
      
      const processedLines = lines.map(l => ({
        ...l,
        SiteIndentLineID: l.SiteIndentLineID ? parseInt(l.SiteIndentLineID) : null,
        FeederId: l.FeederId ? parseInt(l.FeederId) : null,
        SurveyQty: parseFloat(l.SurveyQty) || 0,
        IndentQty: parseFloat(l.IndentQty) || 0,
        QtyToIssue: parseFloat(l.QtyToIssue) || 0,
        IssueLineNumber: parseInt(l.IssueLineNumber) || 0
      }));

      const payload = {
        header: {
          ...header,
          IssueHeaderNo: (initialData && initialData.header && initialData.header.IssueHeaderID) ? header.IssueHeaderNo : nextIssueNumber,
          SiteIndentID: header.SiteIndentID ? parseInt(header.SiteIndentID) : null,
          SurveyBOQID: header.SurveyBOQID ? parseInt(header.SurveyBOQID) : null, 
          ProjectHeaderId: header.ProjectHeaderId ? parseInt(header.ProjectHeaderId) : null,
          FromFeederId: header.FromFeederId ? parseInt(header.FromFeederId) : null,
          ToFeederId: header.ToFeederId ? parseInt(header.ToFeederId) : null
        },
        lines: processedLines
      };

      const sourceData = initialData && initialData.header ? initialData.header : initialData;
      const editId = sourceData ? sourceData.IssueHeaderID : null;

      const isEditMode = !!editId;
      const url = isEditMode ? `${API_URL}/material-issue/${editId}` : `${API_URL}/material-issue`;
      const method = isEditMode ? 'PUT' : 'POST';

    const res = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(payload)
    });

    if (!res.ok) {
        const errorData = await res.json();
        console.log("error message ", errorData);
        
        // Create a custom error that includes the full response data
        const error = new Error(errorData.message || 'Failed to save data.');
        error.errors = errorData.errors || null;
        throw error;
    }

      const result = await res.json(); 

       if (onFormSuccess) { 
        onFormSuccess(result, isEditMode ? 'Updated' : 'Created');
      }

      onClose();

    } catch (error) {
    // Check if it's a validation error with line details
      if (error.message && error.message.includes('exceeds remaining') && error.errors) {
          // Update lines with correct RemainingQty and highlight
          const newLines = [...lines];
          const newHighlightedRows = [];
          
          error.errors.forEach(err => {
              const lineIndex = newLines.findIndex(l => l.SiteIndentLineID === err.SiteIndentLineID);
              if (lineIndex !== -1) {
                  newLines[lineIndex].RemainingQty = err.RemainingQty;
                  newHighlightedRows.push(lineIndex);
              }
          });
          
          setLines(newLines);
          setHighlightedRows(newHighlightedRows);
          
          // Build detailed error message
          const errorMessages = error.errors.map(err => 
              `Line ${err.LineNumber} (${err.ProductCode}): Requested ${err.RequestedQty}, Available ${err.RemainingQty}, Excess ${err.ExcessQty}`
          ).join('\n');
          
          showToast(errorMessages, 'error', 'Quantity Exceeded');
      } else {
          showToast(error.message || 'Error saving data', 'error', 'Submission Error');
      }
    } finally {
      setIsSubmitting(false);
      setLoading(false);
    }
  };

    const showToast = (message, type = 'error', title = 'Validation Error', duration = 4000) => {
    setToast({ open: true, message, type, title });

    setTimeout(() => {
      setToast(prev => ({ ...prev, open: false }));
    }, duration);
  };


  const ResizableTh = ({ width, onResize, children, align = 'left' }) => (
    <th
      style={{ width }}
      className={`relative p-2 ${align === 'right' ? 'text-right' : ''} select-none`}
    >
      {children}
      <div
        className="absolute top-0 right-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400"
        onMouseDown={(e) => onResize(e.clientX)}
      />
    </th>
  );

  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };

  // Place this before the return statement
const selectedFromFeeder = feeders.find(f => f.FeederId == header.FromFeederId);
const selectedToFeeder = feeders.find(f => f.FeederId == header.ToFeederId);

  if (!open) return null;

  return (
    <>
      <div className={`fixed inset-0 z-50 flex justify-center transition-all duration-300 ease-in-out ${open ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
        <style>
          {`
            .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
            .hide-scrollbar::-webkit-scrollbar { display: none; }
            .custom-scrollbar::-webkit-scrollbar { height: 8px; }
            .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 10px; }
            .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
            .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
            button:focus, select:focus, input:focus, textarea:focus { outline: 2px solid #4299e1; outline-offset: 2px; }
            .uppercase-input { text-transform: uppercase; }
            /* Chrome, Edge, Safari */
            input[type=number]::-webkit-outer-spin-button,
            input[type=number]::-webkit-inner-spin-button {
              -webkit-appearance: none;
              margin: 0;
            }

            /* Firefox */
            input[type=number] {
              -moz-appearance: textfield;
            }
            .resizable-table {
              table-layout: fixed;
              min-width: max-content;
            }
              @keyframes toastIn {
              from {
                opacity: 0;
                transform: translate(-50%, -16px) scale(0.98);
              }
              to {
                opacity: 1;
                transform: translate(-50%, 0) scale(1);
              }
            }

            @keyframes toastProgress {
              from { width: 100%; }
              to { width: 0%; }
            }

            .animate-toastIn {
              animation: toastIn 0.25s ease-out;
            }

            .animate-toastProgress {
              animation: toastProgress 4s linear forwards;
            }
              // ADD THIS INSIDE THE STYLE TAG:
            .highlight-row {
                animation: highlightPulse 2s ease-in-out;
            }

            @keyframes highlightPulse {
                0%, 100% { background-color: rgba(254, 226, 226, 0.5); }
                50% { background-color: rgba(254, 226, 226, 1); }
            }
          `}
        </style>
        <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
        <div className={`bg-white shadow-2xl w-full flex flex-col transition-transform transform-gpu duration-300 ease-in-out ${
          open ? 'translate-x-0' : 'translate-x-full'
        } ${ isMaximized ? 'h-full max-w-full' : 'h-full max-w-6xl' }`} tabIndex={0}>
          
          {/* --- MODAL HEADER --- */}
          <div className="flex-shrink-0 p-8 pb-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-[#384959]">
                {(initialData && initialData.header && initialData.header.IssueHeaderID) ? 'Edit Material Issue' : 'Create Material Issue'}
              </h2>
              <div className="flex items-center gap-2">
                <button className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" onClick={toggleMaximize}>
                  {isMaximized ? <MinimizeIcon size={20} /> : <MaximizeIcon size={20} />}
                </button>
                <button className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" onClick={onClose}>
                  <CloseIcon size={24} />
                </button>
              </div>
            </div>
          </div>

          {/* --- FORM BODY --- */}
          <div className="flex-grow overflow-y-auto hide-scrollbar px-8">
            <form onSubmit={handleSubmit} className="space-y-6 pb-6">
              
              {/* --- HEADER SECTION --- */}
              <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                <h3 className="text-lg font-semibold text-[#384959] mb-4">Material Issue Information</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Issue No</label>
                    <input 
                      type="text" 
                      value={(initialData && initialData.header) ? header.IssueHeaderNo : nextIssueNumber} 
                      disabled 
                      className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500 font-medium" 
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Site Indent <span className='text-red-600'>*</span></label>
                    <SearchableDropdown
                      options={siteIndents}
                      value={header.SiteIndentID}
                      onChange={handleSiteIndentChange}
                       disabled={isHeaderLocked} 
                      placeholder="Select Site Indent..."
                      isLoading={filterLoading}
                      getOptionLabel={(si) => `${si.SiteIndentNo} - ${si.ProjectNo || ''} (${si.Status || 'Open'})`}
                      getOptionValue={(si) => si.SiteIndentID}
                      getOptionSecondaryLabel={(si) => si.Date ? new Date(si.Date).toLocaleDateString() : ''}
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Survey BOQ</label>
                    <SearchableDropdown
                      options={surveyBOQs}
                      value={header.SurveyBOQID}
                      onChange={(val) => setHeader({...header, SurveyBOQID: val, SiteIndentID: null, SiteIndentNo: ''})}
                       disabled={isHeaderLocked} 
                      placeholder="Select Survey BOQ..."
                      getOptionLabel={(b) => `${b.SurveyBOQNo} - ${b.ProjectCode || ''}`}
                      getOptionValue={(b) => b.SurveyBOQID}
                      getOptionSecondaryLabel={(b) => b.Circle ? `${b.Circle} / ${b.District}` : ''}
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">Issue Date</label>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={header.IssueDate ? dayjs(header.IssueDate) : null}
                      onChange={(newValue) => {
                        setHeader({
                          ...header,
                          IssueDate: newValue ? newValue.format('YYYY-MM-DD') : ''
                        });
                      }}
                      disabled={isHeaderLocked} 
                      format="DD/MM/YYYY"
                      slotProps={{
                        textField: {
                          size: 'small',
                          className: 'w-full',
                          required: true
                        }
                      }}
                    />
                  </LocalizationProvider>
                </div>

                  {/* Approval/BC Status */}
                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]"> Status</label>
                  <div className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-[#F1F5F9]">
                    {!initialData ? (
                      <span className="text-xs font-medium text-gray-600 italic">Open</span>
                    ) : (header.PostSiteIndent === 1 || header.PostSiteIndent === true) ? (
                      <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded border border-green-200">
                        Material Issued Successfully
                      </span>
                    ) : (header.SendToBC === 1 || header.SendToBC === true || header.SendToBc === 1 || header.SendToBc === true) ? (
                      <span className="text-xs font-medium text-purple-700 bg-purple-50 px-2 py-1 rounded border border-purple-200">
                        Sent to BC (Ready to Post)
                      </span>
                    ) : header.ApprovalStatus === 'PENDING' ? (
                      <span className="text-xs font-medium text-amber-700 bg-amber-50 px-2 py-1 rounded border border-amber-200">
                        Pending Approval (Level {header.CurrentLevel || 1})
                      </span>
                    ) : header.ApprovalStatus === 'REJECTED' ? (
                      <span className="text-xs font-semibold text-red-600 bg-red-50 px-2 py-1 rounded border border-red-200">
                        Rejected
                      </span>
                    ) : header.ApprovalStatus === 'APPROVED' ? (
                      <span className="text-xs font-medium text-blue-700 bg-blue-50 px-2 py-1 rounded border border-blue-200">
                        Approved (Ready to Send BC)
                      </span>
                    ) : (
                      <span className="text-xs text-gray-500 italic">Open</span>
                    )}
                  </div>
                </div>

                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Vendor</label>
                    <SearchableDropdown
                      options={vendors}
                      value={header.VendorCode}
                      onChange={(val) => setHeader({...header, VendorCode: val, SiteIndentID: null, SiteIndentNo: ''})}
                       disabled={isHeaderLocked} 
                      placeholder="Select Vendor"
                      getOptionLabel={(v) => v.vendor_name || v.VendorCode || v.name}
                      getOptionValue={(v) => v.vendor_code || v.VendorCode || v.code}
                      getOptionSecondaryLabel={(v) => v.vendor_code || v.VendorCode}
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Project</label>
                    <SearchableDropdown
                      options={projects}
                      value={header.ProjectNo}
                      onChange={(val) => setHeader({...header, ProjectNo: val, SiteIndentID: null, SiteIndentNo: ''})}
                       disabled={isHeaderLocked} 
                      placeholder="Select Project"
                      getOptionLabel={(p) => `${p.No} - ${p.Description}`}
                      getOptionValue={(p) => p.No}
                      getOptionSecondaryLabel={(p) => p.No}
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">
                      From Feeder
                      {isLoadingStockData && <span className="ml-2 text-blue-500 text-xs">(Loading stock data...)</span>}
                      </label>
                    <SearchableDropdown
                      options={feeders}
                      value={header.FromFeederId}
                      onChange={(val) => setHeader({...header, FromFeederId: val, SiteIndentID: null, SiteIndentNo: ''})}
                       disabled={isHeaderLocked} 
                      placeholder="Select Feeder"
                      getOptionLabel={(f) => `${f.Code} - ${f.Description}`}
                      getOptionValue={(f) => f.FeederId}
                      getOptionSecondaryLabel={(f) => f.Code}
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">From Feeder Address </label>
                  <input 
                    type="text" 
                    value={selectedFromFeeder?.Address || header.FromFeederAddress1 || ''} 
                    readOnly 
                    className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500 uppercase-input" 
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">From Feeder GST</label>
                  <input 
                    type="text" 
                    value={selectedFromFeeder?.Gst_Registration_No || header.FromFeederGst || ''} 
                    readOnly 
                    className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500 uppercase-input" 
                  />
                </div>
               <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">Activity</label>
                  <input 
                    type="text" 
                    // Bind to the Name field (Display)
                    value={header.ActivityName || ''} 
                    readOnly 
                    className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500 uppercase-input" 
                  />
                </div>

              <div className="flex flex-col gap-2">
                <label className="block text-sm font-medium text-[#64748b]">Sub Activity</label>
                <input 
                  type="text" 
                  // Bind to the Name field (Display)
                  value={header.SubActivityName || ''} 
                  readOnly 
                  className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500 uppercase-input" 
                />
              </div>

                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">To Feeder</label>
                    <SearchableDropdown
                      options={feeders}
                      value={header.ToFeederId}
                      onChange={(val) => setHeader({...header, ToFeederId: val, SiteIndentID: null, SiteIndentNo: ''})}
                       disabled={isHeaderLocked} 
                      placeholder="Select Feeder"
                      getOptionLabel={(f) => `${f.Code} - ${f.Description}`}
                      getOptionValue={(f) => f.FeederId}
                      getOptionSecondaryLabel={(f) => f.Code}
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">To Feeder Address </label>
                    <input 
                      type="text" 
                      value={selectedToFeeder?.Address || header.ToFeederAddress1 || ''} 
                      readOnly 
                      className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500 uppercase-input" 
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">To Feeder GST</label>
                    <input 
                      type="text" 
                      value={selectedToFeeder?.Gst_Registration_No || header.ToFeederGst || ''} 
                      readOnly 
                      className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500 uppercase-input" 
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Circle</label>
                    <input type="text" value={header.Circle} readOnly className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500 uppercase-input" />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">District</label>
                    <input type="text" value={header.District} readOnly className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500 uppercase-input" />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Division</label>
                    <input type="text" value={header.Division} readOnly className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500 uppercase-input" />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Sub Division</label>
                    <input type="text" value={header.SubDivision} readOnly className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500 uppercase-input" />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Sub Station</label>
                    <input type="text" value={header.SubStation} readOnly className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500 uppercase-input" />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Feeder Engineer Name</label>
                    <input type="text" value={header.FeederEngineerName} readOnly className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500" />
                  </div>

                  <div className="flex flex-col gap-2 md:col-span-2 lg:col-span-2">
                    <label className="block text-sm font-medium text-[#64748b]">Remarks</label>
                    <textarea value={header.Remarks} onChange={e => setHeader({...header, Remarks: e.target.value})} rows="2" className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]"></textarea>
                  </div>
                </div>
              </div>

              {/* --- LINES SECTION --- */}
              <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-[#384959]">Issue Lines</h3>
                  {highlightedRows.length > 0 && (
                      <button
                          type="button"
                          onClick={() => setHighlightedRows([])}
                          className="px-3 py-1.5 text-sm bg-amber-100 text-amber-800 rounded-lg hover:bg-amber-200 transition-colors flex items-center gap-2"
                      >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          Clear Highlights ({highlightedRows.length} rows)
                      </button>
                  )}
              </div>
                
                <div className="overflow-x-auto custom-scrollbar">
                  <table className="resizable-table border border-[#E2E8F0] ">
                    <thead>
                      <tr className="bg-[#BDDDFC] text-[#384959]">
                        <ResizableTh width={columnWidths.lineNo} onResize={(x) => startResize('lineNo', x)} align="center">Line No</ResizableTh>
                        <ResizableTh width={columnWidths.productCode} onResize={(x) => startResize('productCode', x)}>Product Code</ResizableTh>
                        <ResizableTh width={columnWidths.taskNo} onResize={(x) => startResize('taskNo', x)}>Task No</ResizableTh>
                        <ResizableTh width={columnWidths.desc} onResize={(x) => startResize('desc', x)}>Description</ResizableTh>
                         <ResizableTh width={columnWidths.uom} onResize={(x) => startResize('uom', x)}>UOM</ResizableTh>
                           <ResizableTh width={columnWidths.activity} onResize={(x) => startResize('activity', x)}>
                          Activity
                        </ResizableTh>
                        <ResizableTh width={columnWidths.subActivity} onResize={(x) => startResize('subActivity', x)}>
                          Sub Activity
                        </ResizableTh>
                        <ResizableTh width={columnWidths.availStock} onResize={(x) => startResize('availStock', x)} align="right">
                          Avail Stock
                        </ResizableTh>
                        <ResizableTh width={columnWidths.surveyQty} onResize={(x) => startResize('surveyQty', x)} align="right">Survey Qty</ResizableTh>
                        <ResizableTh width={columnWidths.indentQty} onResize={(x) => startResize('indentQty', x)} align="right">Indent Qty</ResizableTh>
                        {/* <ResizableTh width={columnWidths.remainingQty} onResize={(x) => startResize('remainingQty', x)} align="right">Remaining Qty</ResizableTh> */}
                        <ResizableTh width={columnWidths.qtyToIssue} onResize={(x) => startResize('qtyToIssue', x)} align="right">Qty To Issue</ResizableTh>
                        <ResizableTh width={columnWidths.remark} onResize={(x) => startResize('remark', x)}>
                          Remark
                        </ResizableTh>
                       
                        {/* <ResizableTh width={columnWidths.action} onResize={(x) => startResize('action', x)} align="center">Action</ResizableTh> */}
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((line, idx) => {
                           const isLineLocked =  line.BcSuccess === true || line.BcSuccess === 1;
                           // --- ADD THIS LOGIC HERE (Before return) ---
                        const selectedActivityObj = projectActivities.find(a => a.ActivityId == line.ActivityId);
                        const activityDisplay = selectedActivityObj 
                          ? `${selectedActivityObj.Code} - ${selectedActivityObj.Description}` 
                          : '';

                        const selectedSubActivityObj = projectActivities.find(a => a.ActivityId == line.SubActivityId);
                        const subActivityDisplay = selectedSubActivityObj 
                          ? `${selectedSubActivityObj.Code} - ${selectedSubActivityObj.Description}` 
                          : '';
                        return(
                        <tr 
                            key={idx} 
                            className={`border-t border-[#E2E8F0] transition-colors duration-300 ${
                                highlightedRows.includes(idx) 
                                    ? 'bg-red-100 border-l-4 border-l-red-500' 
                                    : ''
                            }`}
                        >
                          <td className="p-2 text-center" style={{width: columnWidths.lineNo}}>
                            <input type="number" value={line.IssueLineNumber} onChange={e => handleLineChange(idx, 'IssueLineNumber', e.target.value)} className="w-16 border border-[#E2E8F0] rounded-lg px-2 py-1 text-center focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]" readOnly />
                          </td>
                          <td className="p-2" style={{width: columnWidths.productCode}}>
                            <input type="text" value={line.ProductCode} onChange={e => handleLineChange(idx, 'ProductCode', e.target.value)} className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] uppercase-input" readOnly />
                          </td>
                          <td className="p-2" style={{width: columnWidths.taskNo}}>
                            <input type="text" value={line.TaskNo} onChange={e => handleLineChange(idx, 'TaskNo', e.target.value)} className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]"
                            readOnly />
                          </td>
                          <td className="p-2" style={{width: columnWidths.desc}}>
                            <input type="text" value={line.Description} onChange={e => handleLineChange(idx, 'Description', e.target.value)} className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]" readOnly />
                          </td>
                           <td className="p-2" style={{width: columnWidths.uom}}>
                            <input type="text" value={line.UnitOfMeasure} onChange={e => handleLineChange(idx, 'UnitOfMeasure', e.target.value)} className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 uppercase-input" readOnly />
                          </td>
                           <td className="p-2" style={{ width: columnWidths.activity }}>
                              <input 
                                type="text" 
                                value={activityDisplay}
                                readOnly 
                                disabled
                                className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] disabled:bg-gray-100 pointer-events-none" 
                              />
                          </td>
                          <td className="p-2" style={{ width: columnWidths.subActivity }}>
                              <input 
                                type="text" 
                                value={subActivityDisplay}
                                readOnly 
                                disabled
                                className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] disabled:bg-gray-100 pointer-events-none" 
                              />
                          </td> 
                           <td className="p-2" style={{ width: columnWidths.availStock }}>
                            <input 
                              type="number" 
                              inputMode="decimal"  
                              step="0.000001" 
                              value={line.AvailableStock} 
                              readOnly 
                              className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 text-right focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] pointer-events-none bg-gray-50 text-gray-600 font-medium" 
                            />
                          </td>
                          <td className="p-2" style={{width: columnWidths.surveyQty}}>
                            <input type="number" inputMode="decimal"  step="0.000001" value={line.SurveyQty} disabled className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 text-right bg-gray-50 text-gray-500" readOnly />
                          </td>
                          <td className="p-2" style={{width: columnWidths.indentQty}}>
                            <input type="number" inputMode="decimal"  step="0.000001" value={line.IndentQty} disabled className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 text-right bg-gray-50 text-gray-500" readOnly />
                          </td>
                          {/* <td className="p-2" style={{width: columnWidths.remainingQty}}>
                            <input type="number" step="0.01" value={line.RemainingQty} disabled className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 text-right bg-gray-50 text-gray-500 font-semibold" />
                          </td> */}
                          <td className="p-2" style={{width: columnWidths.qtyToIssue}}>
                            <input type="number"
                             inputMode="decimal"  step="0.000001"  value={line.QtyToIssue} onChange={e => handleLineChange(idx, 'QtyToIssue', e.target.value)}  
                             className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 text-right focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]" onFocus={(e) => e.target.select()}/>
                          </td>

                          <td className="p-2" style={{width: columnWidths.remark}}>
                          <input 
                            type="text"
                            value={line.Remark || ''}
                            onChange={e => handleLineChange(idx, 'Remark', e.target.value)}
                            className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]"
                            
                          />
                        </td>
                          {/* <td className="p-2" style={{width: columnWidths.uom}}>
                            <input type="text" value={line.UnitOfMeasure} onChange={e => handleLineChange(idx, 'UnitOfMeasure', e.target.value)} className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 uppercase-input" readOnly />
                          </td> */}
                          {/* <td className="p-2 text-center" style={{width: columnWidths.action}}>
                            <button type="button" className="text-red-500 hover:text-red-700 transition-colors" onClick={() => removeLine(idx)} disabled={lines.length === 1}>
                              <TrashIcon />
                            </button>
                          </td> */}
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                
                <div className="mt-6 bg-white p-4 rounded-lg border border-[#E2E8F0]">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-500">Total Lines</span>
                      <span className="text-base font-semibold">{lines.length}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-500">Total Qty To Issue</span>
                      <span className="text-base font-semibold text-lg text-blue-600">
                        {lines.reduce((acc, line) => acc + (parseFloat(line.QtyToIssue) || 0), 0).toFixed(6)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>

          {/* --- FOOTER --- */}
          <div className="flex-shrink-0 p-8 pt-0">
            <div className="flex justify-end gap-3">
              <button type="button" className="px-6 py-3 rounded-lg text-[#64748b] font-semibold transition-all duration-300 ease-in-out hover:bg-[#F1F5F9]" onClick={onClose}>
                Cancel
              </button>
              <button 
                type="submit" 
                className="px-6 py-3 rounded-lg text-white font-semibold shadow-lg transition-all duration-300 ease-in-out transform hover:scale-105 bg-[#6A89A7] hover:bg-[#88BDF2] disabled:opacity-50 disabled:cursor-not-allowed flex items-center" 
                disabled={loading || isSubmitting} 
                onClick={handleSubmit}
              >
                {loading || isSubmitting ? 'Saving...' : (
                  <>
                    <SaveIcon className="mr-2" size={18} />
                    {(initialData && initialData.header && initialData.header.IssueHeaderID) ? 'Update Issue' : 'Save Issue'}
                  </>
                )}
              </button>
            </div>
          </div>

        </div>
      </div>


            {/* --- TOP CENTER TOAST --- */}
      {toast.open && (
        <div className="fixed top-5 left-1/2 -translate-x-1/2 z-[99999] animate-toastIn">
          <div
            className={`
              relative w-[420px] rounded-2xl shadow-2xl border backdrop-blur-md overflow-hidden
              ${toast.type === 'error' && 'bg-red-50/90 border-red-200'}
              ${toast.type === 'warning' && 'bg-amber-50/90 border-amber-200'}
              ${toast.type === 'success' && 'bg-green-50/90 border-green-200'}
            `}
          >
            {/* Content */}
            <div className="flex gap-4 px-5 py-4">
              {/* Icon */}
              <div className="pt-1">
                {toast.type === 'error' && (
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-red-100">
                    <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                  </div>
                )}

                {toast.type === 'warning' && (
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-amber-100">
                    <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                      <line x1="12" y1="9" x2="12" y2="13" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                  </div>
                )}
                
                {toast.type === 'success' && (
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-green-100">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                      <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                  </div>
                )}
              </div>

              {/* Text */}
              <div className="flex-1">
                <div className="font-semibold text-sm text-gray-800 mb-1">
                  {toast.title}
                </div>
                <div className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">
                  {toast.message}
                </div>
              </div>
            </div>

            {/* Progress bar */}
            <div
              className={`
                absolute bottom-0 left-0 h-[3px] animate-toastProgress
                ${toast.type === 'error' && 'bg-red-500'}
                ${toast.type === 'warning' && 'bg-amber-500'}
                ${toast.type === 'success' && 'bg-green-500'}
              `}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default MaterialIssueFormModal;